import { useState } from 'react';
import {
  useAppAdmins,
  useGrantAppAdmin,
  useMe,
  useRevokeAppAdmin,
} from '@/lib/queries';
import type { AppAdmin, UserSearchHit } from '@/lib/types';
import { Avatar } from '@/components/Avatar';
import { UserPicker } from '@/components/UserPicker';

function AdminChip({
  admin,
  isSelf,
  onRemove,
  pending,
}: {
  admin: AppAdmin;
  isSelf: boolean;
  onRemove: () => void;
  pending: boolean;
}) {
  return (
    <div className="flex items-center gap-3 border border-ink-800 bg-ink-900/60 py-1.5 pl-1.5 pr-2">
      <Avatar upn={admin.upn} fullName={admin.display_name} size="sm" />
      <div className="min-w-0">
        <div className="truncate text-sm text-ink-100">{admin.display_name}</div>
        <div className="truncate text-[0.65rem] text-ink-500">{admin.upn}</div>
      </div>
      {isSelf ? (
        <span className="ml-2 text-[0.65rem] uppercase tracking-widest text-ink-500">tú</span>
      ) : (
        <button
          type="button"
          disabled={pending}
          onClick={onRemove}
          title="Revocar"
          className="ml-2 grid h-6 w-6 place-items-center text-ink-500 transition-colors hover:bg-ink-800 hover:text-ink-100 disabled:opacity-50"
        >
          <svg width="10" height="10" viewBox="0 0 14 14" aria-hidden="true">
            <path d="M1 1l12 12M13 1L1 13" fill="none" stroke="currentColor" strokeWidth="1.5" />
          </svg>
        </button>
      )}
    </div>
  );
}

export function AppAdminsCard() {
  const me = useMe();
  const admins = useAppAdmins(true);
  const grant = useGrantAppAdmin();
  const revoke = useRevokeAppAdmin();
  const [pickerOpen, setPickerOpen] = useState(false);

  const items = admins.data?.items ?? [];

  return (
    <section className="panel">
      <header className="flex items-center justify-between border-b border-ink-800 px-6 py-4">
        <div>
          <div className="label">Acceso de administración</div>
          <h2 className="mt-1 text-lg font-light text-ink-50">Global admins</h2>
          <p className="mt-1 max-w-2xl text-xs text-ink-500">
            Los administradores ven todas las filiales, gestionan empleados, asignan managers y
            editan la documentación.
          </p>
        </div>
        <button
          type="button"
          onClick={() => setPickerOpen(true)}
          className="border border-ink-700 px-3 py-1.5 text-xs text-ink-200 transition-colors hover:bg-ink-800"
        >
          + Añadir
        </button>
      </header>

      <div className="px-6 py-5">
        {admins.isLoading ? (
          <div className="py-6 text-center text-ink-500">Cargando…</div>
        ) : items.length === 0 ? (
          <div className="py-6 text-center text-ink-500">
            No hay administradores asignados (excepto tú).
          </div>
        ) : (
          <div className="flex flex-wrap gap-2">
            {items.map((a: AppAdmin) => (
              <AdminChip
                key={a.user_oid}
                admin={a}
                isSelf={a.user_oid === me.data?.oid}
                pending={
                  revoke.isPending && revoke.variables === a.user_oid
                }
                onRemove={() => {
                  if (confirm(`Revocar acceso de administrador a ${a.display_name}?`)) {
                    revoke.mutate(a.user_oid);
                  }
                }}
              />
            ))}
          </div>
        )}
        {grant.isError ? (
          <div className="mt-3 text-xs text-red-400">
            No se pudo conceder el rol. Verifica permisos.
          </div>
        ) : null}
      </div>

      <UserPicker
        open={pickerOpen}
        title="Conceder rol de administrador"
        excludeOids={items.map((a: AppAdmin) => a.user_oid)}
        onClose={() => setPickerOpen(false)}
        onPick={(u: UserSearchHit) => {
          grant.mutate(u.oid, { onSuccess: () => setPickerOpen(false) });
        }}
      />
    </section>
  );
}
